import { FunctionComponent, useState } from "react";
import {
  Alert,
  Skeleton,
  Box,
  Dialog,
  Fade,
  Grid2,
  IconButton,
  useMediaQuery,
  useTheme
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "../../../app/helpers";
import { RootState } from "../../../app/rootReducer";
import {
  SelectedZoneOrWorkplaceType,
  setCapacityDataEmpty,
  setCurrentFloors,
  setFilterSelected,
  setIsSelected,
  setSelectedByAttFilter,
  setSelectedZoneOrWorkplace
} from "../../../features/Reports/slices/report.slice";
import FloorSelector from "./floor-selector.component";
import { AreaChartsReport } from "./area-chart.component";
import WeekdayBars from "./weekday-report-chart.component";
import { FloorPlanMemo, ReportEntities } from "../../../features/Reports/typings/reports.types";
import { currentEntityData } from "./functions/facility-report.functions";
import FacilityReportSVG from "../FacilityReport/facility-report-svg.component";
import CapacityPerHourReport from "./capacity-per-hour-report.component";
import { CapacityFilterAndSelector } from "../CapacityFilter/DateAndTypeSelector/facility-report-filter-and-selector.component";
import { initialWeekdays } from "../../../features/Reports/initialWeekdays.constant";

interface OwnProps {
  zoneBookingReport: boolean;
  floorData: FloorPlanMemo;
}

type Props = OwnProps;

const FacilityReportDialog: FunctionComponent<Props> = props => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const {
    capacitySelectedFloors,
    currentFloors,
    capacitySelectedWorkplaceOrZone,
    capacitySelectedByAttFilter,
    selectedZoneOrWorkplace,
    selectableEntities,
    selectedLocation
  } = useSelector((state: RootState) => state.report);

  const [isFloorFullMode, setIsFloorFullMode] = useState<{ index: number | null; open: boolean }>({
    index: null,
    open: false
  });

  const getFloorNameById = (id: number) => {
    // returns selected one's name when selecting place or zone
    if (selectedZoneOrWorkplace?.open) {
      return selectedZoneOrWorkplace.name;
    }

    const entities = selectableEntities as ReportEntities[];
    if (!entities) return "";

    return (
      entities
        .find((entity: { id: number }) => entity.id === selectedLocation?.id)
        ?.floors?.find(floor => floor.inventoryId === id)?.name ?? ""
    );
  };

  const entityData = currentEntityData(
    selectedZoneOrWorkplace,
    capacitySelectedWorkplaceOrZone,
    capacitySelectedByAttFilter,
    currentFloors,
    capacitySelectedFloors
  );

  const capacityObjectsMapped = entityData?.map(object => ({
    id: object?.id ?? 1,
    data: object?.capacity,
    label: getFloorNameById(object?.id ?? 1)
  }));

  if (!currentFloors.some(floor => floor.open)) return null;

  const isReportFetched =
    !!currentFloors[0].id &&
    props.floorData[currentFloors[0].id] &&
    entityData?.[0]?.capacity?.capacityPerformancePerWorkplace &&
    !!capacityObjectsMapped;

  const handleClose = () => {
    dispatch(setCurrentFloors([]));
    setIsFloorFullMode({ index: null, open: false });
    dispatch(setIsSelected(false));
    dispatch(
      setSelectedZoneOrWorkplace({
        id: [],
        name: [],
        type: SelectedZoneOrWorkplaceType.ZONE,
        open: false,
        floorInventoryId: 0,
        bookingType: undefined
      })
    );
    dispatch(
      setSelectedByAttFilter({
        id: [],
        name: [],
        type: SelectedZoneOrWorkplaceType.ZONE,
        open: false,
        floorInventoryId: -1,
        bookingType: undefined
      })
    );
    dispatch(
      setFilterSelected({
        selectedDepartmentIds: [],
        selectedWeekdays: initialWeekdays,
        selectedAttributes: []
      })
    );
    dispatch(setCapacityDataEmpty(true));
  };

  return (
    <Dialog
      fullScreen
      onClose={handleClose}
      aria-labelledby="simple-dialog-title"
      open={currentFloors.some(floor => floor.open)}
      slotProps={{ paper: { sx: { backgroundImage: "none" } } }}
    >
      <Grid2 container sx={{ width: "100%", height: "auto", padding: "2%" }}>
        <Grid2 data-testid="dialog-filter-header" sx={{ display: "flex", width: "100%" }}>
          {/* floor single selector, date/place/zone type selector, and other group slideout filter */}
          <CapacityFilterAndSelector
            isGroupFilterUse
            zoneBookingReport={props.zoneBookingReport}
            entityData={entityData}
            isReportFetched={isReportFetched}
            floorData={props.floorData}
          />

          {/* close dialog */}
          <IconButton
            data-testid="close-report-dialog"
            sx={{ position: "absolute", top: "20px", right: "20px" }}
            onClick={handleClose}
            size="large"
          >
            <CloseIcon fontSize="medium" />
          </IconButton>
        </Grid2>

        <Grid2
          container
          justifyContent={"flex-start"}
          direction={"row"}
          sx={{ margin: "10px 0px 0px 0px", width: "100%" }}
          data-testid="facility-report-and-floor-selector-grid"
        >
          {isReportFetched ? (
            <>
              <FacilityReportSVG
                key={props.floorData[currentFloors[0].id].floorData.floorInventoryId}
                floorData={props.floorData}
                zoneBookingReport={props.zoneBookingReport}
                isFloorFullMode={isFloorFullMode}
                setIsFloorFullMode={setIsFloorFullMode}
                entityData={entityData}
              />
              {currentFloors.length === 1 && (
                <Grid2 size={{ xs: 12, md: 6 }}>
                  <Box
                    style={{
                      height: 500,
                      border: `1px solid rgba(0, 0, 0, 0.42)`,
                      borderRadius: 4,
                      display: "flex",
                      alignItems: "center",
                      flexDirection: "column",
                      justifyContent: "center"
                    }}
                  >
                    <Alert severity="info">
                      <Grid2 container direction={"column"}>
                        {t("You can add a floor to compare to")}
                        <FloorSelector zoneBooking={props.zoneBookingReport} />
                      </Grid2>
                    </Alert>
                  </Box>
                </Grid2>
              )}
            </>
          ) : (
            <Skeleton variant="rectangular" width={"100%"} height={"78vh"} />
          )}
        </Grid2>

        {/* two chart report below the area chart at the bottom */}
        <Grid2 size={{ md: 12 }} sx={{ width: "100%" }} data-testid="capacity-per-hour-report-grid">
          {isReportFetched && (
            <Fade
              mountOnEnter
              unmountOnExit
              in={!!entityData[0]?.capacity.capacityPerformancePerDay}
            >
              <Grid2 container sx={{ width: "100%" }}>
                {/* weekdays bar report */}
                <Grid2
                  size={{ sm: 12, md: 6 }}
                  p={isMobile ? 0 : "0 4px 0 0"}
                  sx={{ width: "100%" }}
                >
                  <WeekdayBars data={entityData} />
                </Grid2>

                {/* capacity per hour report */}
                <Grid2
                  size={{ sm: 12, md: 6 }}
                  p={isMobile ? 0 : "0 0 0 4px"}
                  sx={{ width: "100%" }}
                >
                  <CapacityPerHourReport capacityObjects={capacityObjectsMapped} />
                </Grid2>
              </Grid2>
            </Fade>
          )}
        </Grid2>

        {/* area chart report below the floor plan */}
        {isReportFetched && (
          <Grid2
            size={{ md: 12 }}
            data-testid="area-charts-report-grid"
            sx={{ width: "100%", mt: 2, mb: 1 }}
          >
            <AreaChartsReport
              capacityObjects={capacityObjectsMapped}
              width={isFloorFullMode.open ? "100.2%" : "100%"}
            />
          </Grid2>
        )}
      </Grid2>
    </Dialog>
  );
};

export default FacilityReportDialog;
